/*
 * Copyright 2026 North Carolina State University
 */

/**
 * Usage periods on a resource assignment: when the resource was in use (e.g.
 * each freeze-dryer batch). Tracking only - resources are billed by quantity.
 * Dates are stored at UTC noon, so the literal date part is the calendar day.
 */

import { calculateDays } from './invoiceCalculations';

/** YYYY-MM-DD for a date input, from a stored date or ISO string. */
export const toDateInput = (value) => {
  if (!value) return '';
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? '' : value.toISOString().split('T')[0];
  return String(value).split('T')[0];
};

const formatDay = (value) => {
  const [y, m, d] = toDateInput(value).split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/** "Jan 3, 2026 – Jan 6, 2026 (4 days)", or "Jan 3, 2026 – ongoing". */
export const formatUsagePeriod = (period) => {
  if (!period?.start_date) return '';
  if (!period.end_date) return `${formatDay(period.start_date)} – ongoing`;
  const days = calculateDays(toDateInput(period.start_date), toDateInput(period.end_date));
  return `${formatDay(period.start_date)} – ${formatDay(period.end_date)} (${days} day${days === 1 ? '' : 's'})`;
};

/** Editor rows -> request payload, dropping rows with no start date. */
export const toUsagePayload = (rows) => (rows || [])
  .filter(r => r.start_date)
  .map(r => ({ start_date: r.start_date, end_date: r.end_date || null, note: r.note || '' }));

/** Stored periods -> editor rows. */
export const toUsageRows = (periods) => (periods || []).map(p => ({
  start_date: toDateInput(p.start_date),
  end_date: toDateInput(p.end_date),
  note: p.note || ''
}));

/** First problem with the editor rows, or null. */
export const validateUsageRows = (rows) => {
  for (const [i, r] of (rows || []).entries()) {
    if (!r.start_date && (r.end_date || r.note)) return `Usage period ${i + 1}: enter a start date`;
    if (r.start_date && r.end_date && r.end_date < r.start_date) return `Usage period ${i + 1}: end date is before the start date`;
  }
  return null;
};
